/**
 * Genie Runtime — the document that runs a Shader node's code.
 *
 * `buildSrcdoc` returns HTML for a sandboxed iframe (`allow-scripts`, opaque
 * origin). Inside it the runtime provides:
 * - `window.genie`: inputs, params (defaults parsed on the host), canvas info
 * - a bridge to the host over a MessagePort that only this document receives
 *   (see `buildBridge`)
 * - pause that freezes `performance.now()`, so time-based sketches really stop
 * - frame capture that works with WebGL's cleared drawing buffer, also while paused
 * - a 1s heartbeat the host uses to detect a hung sketch (no frame-based
 *   watchdog: a sketch that draws once or is throttled off-screen is idle, not hung)
 * - errors tagged by kind, with line numbers relative to the user's code
 * - every media input in `media/contract.ts`: `window.genie` values for JS
 *   runtimes, and bound uniforms and textures for GLSL
 */

import { CDN_DEFAULTS, CDN_INTEGRITY } from './types';
import type { ExtractedParam, RuntimeMode } from './types';
import { parseParams } from './params';
import { buildBridgeMediaCode } from './media/runtime-bridge-media';
import { buildGlslInputsCode } from './media/runtime-glsl-inputs';

/** JSON that is safe inside an inline <script>. */
function scriptJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}

/** User JS inlined in <script>: a literal `</script` would end the element early. */
function escapeInlineScript(code: string): string {
  return code.replace(/<\/(script)/gi, '<\\/$1');
}

const CODE_LINE_TOKEN = '__GENIE_CODE_LINE__';

// ---------------------------------------------------------------------------
// Bridge (runs first, before any library or user code)
// ---------------------------------------------------------------------------

/**
 * The bridge greets its parent with `genie:hello` and the session the host gave this
 * document; the host answers once per session with a MessagePort, and every other
 * message in both directions goes over that port. A sandboxed frame can navigate
 * itself, and a page it lands on shares the frame's window, so window messages
 * cannot tell that page from the sketch. The port can: it never reaches a new page.
 */
function buildBridge(width: number, height: number, params: ExtractedParam[], session: string): string {
  return `
(function() {
  'use strict';

  var CODE_LINE = ${CODE_LINE_TOKEN};
  var SESSION = ${scriptJson(session)};
  var parent = window.parent;
  var port = null, outbox = [];
  function post(msg) {
    if (port) { port.postMessage(msg); return; }
    // Until the host connects, keep what it must see. Heartbeats can go, and a page opened
    // with no host (a standalone sketch page) must not queue forever.
    if (msg.type !== 'genie:heartbeat' && outbox.length < 64) outbox.push(msg);
  }
  window.__geniePost = post;

  // Time freezes while paused: p5's millis(), three.js clocks and GLSL u_time all read
  // performance.now(), so pausing stops motion instead of skipping ahead on resume.
  var realNow = performance.now.bind(performance);
  var paused = false, pauseStartedAt = 0, pausedTotal = 0;
  performance.now = function() { return (paused ? pauseStartedAt : realNow()) - pausedTotal; };

  var params = ${scriptJson(params)};
  var handlers = {};
${buildBridgeMediaCode()}
  window.__genieParamDefs = params;
  window.genie = {
    inputImage: null,
    inputVideo: null,
    inputText: null,
    inputModel: null,
    audio: silentAudio(),
    params: {},
    // The page's <canvas>; null in p5 mode, where createCanvas() makes its own.
    canvas: document.querySelector('canvas'),
    width: ${width},
    height: ${height},
    pixelRatio: window.devicePixelRatio || 1,
    output: function(handle, data) { post({ type: 'genie:output', handle: handle, data: data }); },
    onInput: function(handle, callback) { (handlers[handle] = handlers[handle] || []).push(callback); },
    modelToThree: modelToThree,
  };
  for (var i = 0; i < params.length; i++) window.genie.params[params[i].name] = params[i].default;

  // --- Animation frames: deferred while paused, one frame released per capture ---
  var nativeRAF = window.requestAnimationFrame.bind(window);
  var deferred = [];
  var hasAnimation = false;
  var capturePending = false;
  var captureFallback = null;
  var captureRequestedAt = 0;
  // Frames requested from the browser that haven't run yet.
  var outstanding = 0;

  function captureNow() {
    if (captureFallback) { clearTimeout(captureFallback); captureFallback = null; }
    var canvas = document.querySelector('canvas');
    if (!canvas) {
      // p5 and CDN-loaded sketches make their canvas after 'ready'; wait for it rather than fail.
      if (Date.now() - captureRequestedAt < 5000) {
        capturePending = true;
        captureFallback = setTimeout(checkCaptureFallback, 100);
        return;
      }
      capturePending = false;
      post({ type: 'genie:error', kind: 'runtime', message: 'No canvas to capture' });
      return;
    }
    capturePending = false;
    try {
      post({ type: 'genie:capture', dataUrl: canvas.toDataURL('image/png') });
    } catch (e) {
      post({ type: 'genie:error', kind: 'runtime', message: 'Frame capture failed: ' + (e && e.message) });
    }
  }

  function runFrame(callback) {
    callback(performance.now());
    if (capturePending) captureNow();
  }

  window.requestAnimationFrame = function(callback) {
    hasAnimation = true;
    outstanding++;
    return nativeRAF(function() {
      outstanding--;
      if (paused && !capturePending) { deferred.push(callback); return; }
      runFrame(callback);
    });
  };

  function releaseDeferred() {
    var queue = deferred;
    deferred = [];
    for (var i = 0; i < queue.length; i++) {
      outstanding++;
      (function(callback) { nativeRAF(function() { outstanding--; runFrame(callback); }); })(queue[i]);
    }
  }

  function checkCaptureFallback() {
    captureFallback = null;
    if (!capturePending) return;
    if (outstanding > 0 && Date.now() - captureRequestedAt < 5000) {
      captureFallback = setTimeout(checkCaptureFallback, 250);
      return;
    }
    captureNow();
  }

  function requestCapture() {
    captureRequestedAt = Date.now();
    if (!hasAnimation) { captureNow(); return; }
    capturePending = true;
    if (paused) releaseDeferred();
    // Sketches that stopped requesting frames (p5 noLoop) still get a capture. While a frame
    // is still on its way, wait for it: a WebGL buffer read outside a frame is blank.
    if (captureFallback) clearTimeout(captureFallback);
    captureFallback = setTimeout(checkCaptureFallback, 250);
  }

  // --- Iframe → host errors ---
  function reportError(message, kind, line, column) {
    var userLine = line && line >= CODE_LINE ? line - CODE_LINE + 1 : undefined;
    post({ type: 'genie:error', kind: kind, message: String(message || 'Unknown error'), line: userLine, column: userLine ? column : undefined });
  }

  // --- Host → iframe: only over the port the host handed this document ---
  function receive(msg) {
    if (!msg || typeof msg.type !== 'string') return;

    if (msg.type === 'genie:input') {
      if (typeof msg.handle === 'string') mediaInput(msg.handle, msg.data === undefined ? null : msg.data);
    } else if (msg.type === 'genie:param' && typeof msg.name === 'string') {
      window.genie.params[msg.name] = msg.value;
    } else if (msg.type === 'genie:control') {
      if (msg.action === 'pause' && !paused) { pauseStartedAt = realNow(); paused = true; mediaPause(); }
      else if (msg.action === 'resume' && paused) { pausedTotal += realNow() - pauseStartedAt; paused = false; releaseDeferred(); mediaResume(); }
      else if (msg.action === 'capture') requestCapture();
    }
  }

  window.addEventListener('message', function(event) {
    if (port || event.source !== parent) return;
    var msg = event.data;
    if (!msg || msg.type !== 'genie:connect' || msg.session !== SESSION || !event.ports || !event.ports[0]) return;
    port = event.ports[0];
    port.onmessage = function(e) { receive(e.data); };
    var queued = outbox;
    outbox = [];
    for (var i = 0; i < queued.length; i++) port.postMessage(queued[i]);
  });

  window.onerror = function(message, source, line, column) {
    reportError(message, 'runtime', line, column);
    return true;
  };
  window.addEventListener('unhandledrejection', function(event) {
    var reason = event.reason;
    reportError('Unhandled promise rejection: ' + (reason && reason.message ? reason.message : String(reason)), 'runtime');
  });
  document.addEventListener('webglcontextlost', function() {
    post({ type: 'genie:error', kind: 'context-lost', message: 'The graphics context was lost (too many live shaders?). Press Restart.' });
  }, true);

  // 'ready' goes out before the runtime library and the user's code run; 'loaded' after both.
  window.addEventListener('load', function() { post({ type: 'genie:loaded' }); });
  setInterval(function() { post({ type: 'genie:heartbeat' }); }, 1000);
  parent.postMessage({ type: 'genie:hello', session: SESSION }, '*');
  post({ type: 'genie:ready' });
})();
`;
}

/** The bridge script alone, with param defaults parsed from `userCode`. */
export function buildGenieRuntimeCode(width: number, height: number, userCode: string, session = ''): string {
  return buildBridge(width, height, parseParams(userCode), session).replace(CODE_LINE_TOKEN, '1');
}

// ---------------------------------------------------------------------------
// GLSL bootstrap (fragment shader on a fullscreen quad)
// ---------------------------------------------------------------------------

function buildGlslBootstrap(width: number, height: number, fragmentSource: string): string {
  return `
(function() {
  var canvas = document.querySelector('canvas');
  canvas.width = ${width};
  canvas.height = ${height};
  var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  function fail(kind, message, line) {
    window.__geniePost({ type: 'genie:error', kind: kind, message: message, line: line });
  }
  if (!gl) { fail('runtime', 'WebGL is not available in this browser'); return; }

  // Passes a [0,1] UV as v_texCoord for shaders written in the common Shadertoy-ish style.
  var vertexSource = 'attribute vec2 a_position;\\nvarying vec2 v_texCoord;\\nvoid main() {\\n  v_texCoord = a_position * 0.5 + 0.5;\\n  gl_Position = vec4(a_position, 0.0, 1.0);\\n}';
  var fragmentSource = ${scriptJson(fragmentSource)};

  function compile(type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      var log = gl.getShaderInfoLog(shader) || 'Shader failed to compile';
      gl.deleteShader(shader);
      var match = /ERROR:\\s*\\d+:(\\d+):/.exec(log);
      throw { log: log, line: match ? Number(match[1]) : undefined };
    }
    return shader;
  }

  var program;
  try {
    program = gl.createProgram();
    gl.attachShader(program, compile(gl.VERTEX_SHADER, vertexSource));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragmentSource));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw { log: gl.getProgramInfoLog(program) || 'Shader failed to link' };
  } catch (e) {
    fail('compile', 'GLSL compile error: ' + (e && e.log ? e.log : String(e)), e && e.line);
    return;
  }
  gl.useProgram(program);

  var buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
  var positionLoc = gl.getAttribLocation(program, 'a_position');
  gl.enableVertexAttribArray(positionLoc);
  gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

${buildGlslInputsCode()}
  // Bind every uniform the runtime doesn't own to genie.params[name] each frame.
  var defs = {};
  (window.__genieParamDefs || []).forEach(function(p) { defs[p.name] = p; });
  var bound = [];
  var uniformRe = /uniform\\s+(?:(?:lowp|mediump|highp)\\s+)?(float|int|bool|vec2|vec3|vec4)\\s+(\\w+)\\s*;/g;
  var m;
  while ((m = uniformRe.exec(fragmentSource)) !== null) {
    if (RESERVED[m[2]]) continue;
    var loc = gl.getUniformLocation(program, m[2]);
    if (loc) bound.push({ type: m[1], name: m[2], loc: loc });
  }

  function hexToRgb(value) {
    var h = String(value).replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return [parseInt(h.substr(0, 2), 16) / 255, parseInt(h.substr(2, 2), 16) / 255, parseInt(h.substr(4, 2), 16) / 255];
  }
  function toNumber(name, value) {
    // Select params arrive as option strings; numeric uniforms receive the option's index.
    var def = defs[name];
    if (typeof value === 'string' && def && def.options) return Math.max(0, def.options.indexOf(value));
    if (typeof value === 'boolean') return value ? 1 : 0;
    var n = Number(value);
    return isFinite(n) ? n : 0;
  }

  function applyParams() {
    for (var i = 0; i < bound.length; i++) {
      var u = bound[i];
      var value = window.genie.params[u.name];
      if (value === undefined || value === null) continue;
      if (u.type === 'float') gl.uniform1f(u.loc, toNumber(u.name, value));
      else if (u.type === 'int') gl.uniform1i(u.loc, Math.round(toNumber(u.name, value)));
      else if (u.type === 'bool') gl.uniform1i(u.loc, value === true || value === 'true' || value === 1 ? 1 : 0);
      else if (Array.isArray(value)) {
        if (u.type === 'vec2') gl.uniform2f(u.loc, value[0] || 0, value[1] || 0);
        else if (u.type === 'vec3') gl.uniform3f(u.loc, value[0] || 0, value[1] || 0, value[2] || 0);
        else gl.uniform4f(u.loc, value[0] || 0, value[1] || 0, value[2] || 0, value[3] === undefined ? 1 : value[3]);
      } else if (typeof value === 'string' && value.charAt(0) === '#') {
        var rgb = hexToRgb(value);
        if (u.type === 'vec3') gl.uniform3f(u.loc, rgb[0], rgb[1], rgb[2]);
        else if (u.type === 'vec4') gl.uniform4f(u.loc, rgb[0], rgb[1], rgb[2], 1);
      }
    }
  }

  // u_mouse is in canvas pixels (like u_resolution), origin bottom-left.
  var mouseX = 0, mouseY = 0;
  canvas.addEventListener('mousemove', function(e) {
    var rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    mouseX = (e.clientX - rect.left) * canvas.width / rect.width;
    mouseY = (rect.height - (e.clientY - rect.top)) * canvas.height / rect.height;
  });

  var start = performance.now();
  function render() {
    requestAnimationFrame(render);
    var seconds = (performance.now() - start) / 1000;
    updateInputs(seconds);
    gl.viewport(0, 0, canvas.width, canvas.height);
    if (uTime) gl.uniform1f(uTime, seconds);
    if (uResolution) gl.uniform2f(uResolution, canvas.width, canvas.height);
    if (uMouse) gl.uniform2f(uMouse, mouseX, mouseY);
    applyInputUniforms();
    applyParams();
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
  render();
})();
`;
}

// ---------------------------------------------------------------------------
// Document
// ---------------------------------------------------------------------------

const CONTENT_SECURITY_POLICY = [
  "default-src 'none'",
  "script-src 'unsafe-inline' 'unsafe-eval' https://cdn.jsdelivr.net",
  "style-src 'unsafe-inline'",
  'img-src data: blob:',
  'media-src data: blob:',
  // data:/blob: only, so sketches cannot fetch, beacon or open sockets. This is not a seal:
  // no CSP directive stops a frame navigating itself, so a sketch can still carry what it
  // was given out in a URL. The bridge's port keeps a page it navigates to from receiving
  // anything more (see buildBridge).
  'connect-src data: blob:',
  'font-src data:',
  'worker-src blob:',
].join('; ');

/**
 * Build the complete srcdoc HTML for the sandboxed iframe.
 *
 * @param mode - Runtime mode (p5, glsl, threejs, raw)
 * @param userCode - The user's source code (a fragment shader in GLSL mode)
 * @param width - Canvas width
 * @param height - Canvas height
 * @param cdnUrls - Optional CDN URLs to load (overrides defaults; no integrity pinning)
 * @param session - Names this document in its handshake with the host (see buildBridge)
 */
export function buildSrcdoc(
  mode: RuntimeMode,
  userCode: string,
  width: number,
  height: number,
  cdnUrls?: string[],
  session = ''
): string {
  const scripts = (cdnUrls ?? CDN_DEFAULTS[mode])
    .map((url) => {
      const integrity = cdnUrls ? undefined : CDN_INTEGRITY[url];
      return integrity
        ? `<script src="${url}" integrity="${integrity}" crossorigin="anonymous"><\/script>`
        : `<script src="${url}" crossorigin="anonymous"><\/script>`;
    })
    .join('\n  ');

  // p5's createCanvas() appends its own canvas. A pre-made one would sit on top
  // (pushing p5's output out of the overflow-hidden viewport) and be the one captured.
  const canvasTag = mode === 'p5' ? '' : `<canvas width="${width}" height="${height}"></canvas>`;

  const head = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta http-equiv="Content-Security-Policy" content="${CONTENT_SECURITY_POLICY}">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 100%; height: 100%; overflow: hidden; background: #000; }
    canvas { display: block; width: 100%; height: 100%; }
  </style>
</head>
<body>
  ${canvasTag}
  <script>${buildBridge(width, height, parseParams(userCode), session)}<\/script>
  ${scripts}
  <script>
`;

  const body = mode === 'glsl' ? buildGlslBootstrap(width, height, userCode) : escapeInlineScript(userCode);
  const tail = `
  <\/script>
  ${mode === 'p5' ? '<script>new p5();<\/script>' : ''}
</body>
</html>`;

  // Runtime errors report document lines; the bridge maps them back to the user's code.
  // GLSL compile errors already carry shader-relative lines.
  const codeLine = mode === 'glsl' ? Number.MAX_SAFE_INTEGER : head.split('\n').length;
  return (head + body + tail).replace(CODE_LINE_TOKEN, String(codeLine));
}
